<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { stageColor } from 'dashboard/components-next/Contacts/contactStageColors';
import { syncedListAttributes } from 'dashboard/components-next/Contacts/syncedAttributes';
import {
  useContactCounts,
  compactCount,
} from 'dashboard/components-next/Contacts/contactCounts';

import Icon from 'dashboard/components-next/icon/Icon.vue';

// Filter row of the contacts screen, 1:1 with the CrmB mockup: "Tipo" and
// "Estado" dropdowns (the fork's list-type contact attributes, applied
// through the regular contact filter pipeline so the active-filter preview
// and "Guardar vista" keep working), a divider, then the pills "Todos" /
// "Activos" / saved segments with their counts.
const emit = defineEmits(['applyAttributeFilters', 'clearFilters']);

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const { accountScopedRoute } = useAccount();
const { counts, refresh } = useContactCounts();

const NEUTRAL_DOT = '#b8c5cf';
const TRIGGER_CLASS =
  'inline-flex items-center gap-[7px] h-7 px-2.5 rounded-[7px] text-[11.5px] whitespace-nowrap max-w-[12rem] transition-colors';
const PILL_CLASS =
  'inline-flex items-center gap-1.5 h-7 px-[11px] rounded-[7px] text-[11.5px] whitespace-nowrap max-w-[14rem] transition-colors';
const PANEL_CLASS =
  'absolute z-30 top-full mt-1.5 ltr:left-0 rtl:right-0 w-[238px] pt-1 pb-1.5 rounded-[10px] border border-n-weak bg-n-solid-2 shadow-[0_10px_28px_rgba(20,40,60,.15)] max-h-80 overflow-y-auto';
const PANEL_TITLE_CLASS =
  'px-3 pt-2 pb-1.5 text-[10.5px] font-semibold tracking-[0.1em] uppercase text-n-slate-10';
const ROW_CLASS =
  'flex items-center gap-[9px] w-full h-[31px] px-3 text-[12.5px] text-left';

const segments = useMapGetter('customViews/getContactCustomViews');
const contactAttributes = useMapGetter('attributes/getContactAttributes');
const appliedFilters = useMapGetter('contacts/getAppliedContactFiltersV4');

// Un filtro por cada atributo sincronizado con etiquetas, en su orden.
const syncedAttributes = computed(() =>
  syncedListAttributes(contactAttributes.value)
);

const openMenu = ref(null);
const toggleMenu = key => {
  openMenu.value = openMenu.value === key ? null : key;
};
const closeMenu = () => {
  openMenu.value = null;
};

// ---- route-derived scope ---------------------------------------------------
const isActiveView = computed(() => route.name === 'contacts_dashboard_active');
const isIndexView = computed(
  () =>
    route.name === 'contacts_dashboard_index' || route.name === 'contacts_edit'
);
const currentSegmentId = computed(() =>
  ['contacts_dashboard_segments_index', 'contacts_edit_segment'].includes(
    route.name
  )
    ? Number(route.params.segmentId)
    : null
);

// ---- attribute filters (Tipo / Estado) --------------------------------------
const appliedValueFor = key => {
  const filter = (appliedFilters.value || []).find(f => f.attributeKey === key);
  if (!filter) return null;
  const [first] = Array.isArray(filter.values)
    ? filter.values
    : [filter.values];
  return first?.id ?? first ?? null;
};

const buildAttributeFilter = (attribute, value) => ({
  attributeKey: attribute.attributeKey,
  attributeModel: 'custom_attributes',
  customAttributeType: attribute.attributeDisplayType,
  filterOperator: 'equal_to',
  values: [{ id: value, name: value }],
  queryOperator: 'and',
});

const setAttributeValue = (attribute, value) => {
  closeMenu();
  if (!attribute) return;
  const others = (appliedFilters.value || []).filter(
    f => f.attributeKey !== attribute.attributeKey
  );
  const next = value
    ? [...others, buildAttributeFilter(attribute, value)]
    : others;
  if (!next.length) {
    emit('clearFilters');
    return;
  }
  emit('applyAttributeFilters', next);
};

// ---- navigation pills -------------------------------------------------------
const goToAll = () => {
  closeMenu();
  router.push(
    accountScopedRoute(
      'contacts_dashboard_index',
      {},
      { page: 1, search: undefined }
    )
  );
};
const goToActive = () => {
  closeMenu();
  router.push(accountScopedRoute('contacts_dashboard_active'));
};
const goToSegment = segmentId => {
  closeMenu();
  router.push(
    accountScopedRoute(
      'contacts_dashboard_segments_index',
      { segmentId },
      { page: 1 }
    )
  );
};

const totalCount = computed(() => compactCount(counts.total));
const segmentCount = segment => compactCount(counts.segments[segment.id]);

const menus = computed(() =>
  syncedAttributes.value.map(attribute => ({
    key: attribute.attributeKey,
    attribute,
    current: appliedValueFor(attribute.attributeKey),
    placeholder: attribute.attributeDisplayName,
    any: t('CONTACTS_LAYOUT.HEADER.SCOPE_FILTER.ALL'),
    showDots: true,
  }))
);

const triggerClass = value =>
  value
    ? 'bg-n-solid-blue text-n-blue-text font-semibold'
    : 'bg-n-alpha-2 text-n-slate-11 font-medium hover:text-n-slate-12';
const dotStyle = value => ({
  backgroundColor: value ? stageColor(value).dot : NEUTRAL_DOT,
});
const rowClass = active =>
  active
    ? 'bg-n-blue-2 text-n-blue-text font-semibold'
    : 'text-n-slate-11 font-medium hover:bg-n-alpha-1';
const pillClass = active =>
  active
    ? 'bg-n-brand text-white font-semibold'
    : 'bg-n-alpha-2 text-n-slate-11 font-medium hover:text-n-slate-12';
const countClass = active => (active ? 'text-white/70' : 'text-n-slate-9');

// Counts: shared cache, refreshed when the segment list changes.
onMounted(() => refresh(segments.value));
watch(
  () => (segments.value || []).map(segment => segment.id).join(','),
  () => refresh(segments.value)
);
</script>

<template>
  <div class="flex flex-wrap items-center gap-1.5 min-w-0">
    <div v-for="menu in menus" :key="menu.key" class="relative">
      <button
        type="button"
        :class="[TRIGGER_CLASS, triggerClass(menu.current)]"
        @click="toggleMenu(menu.key)"
      >
        <span
          class="size-[7px] rounded-full flex-shrink-0"
          :style="dotStyle(menu.current)"
        />
        <span class="truncate">{{ menu.current || menu.placeholder }}</span>
        <Icon
          icon="i-lucide-chevron-down"
          size="size-[11px] flex-shrink-0 opacity-55"
        />
      </button>
      <div
        v-if="openMenu === menu.key"
        v-on-clickaway="closeMenu"
        :class="PANEL_CLASS"
      >
        <div :class="PANEL_TITLE_CLASS">
          {{ menu.attribute.attributeDisplayName }}
        </div>
        <button
          type="button"
          :class="[ROW_CLASS, rowClass(!menu.current)]"
          @click="setAttributeValue(menu.attribute, null)"
        >
          <span
            class="size-[9px] rounded-full flex-shrink-0"
            :style="dotStyle(null)"
          />
          <span class="flex-1 truncate">{{ menu.any }}</span>
          <Icon
            v-if="!menu.current"
            icon="i-lucide-check"
            size="size-3 text-n-blue-text"
          />
        </button>
        <button
          v-for="value in menu.attribute.attributeValues || []"
          :key="value"
          type="button"
          :class="[ROW_CLASS, rowClass(menu.current === value)]"
          @click="setAttributeValue(menu.attribute, value)"
        >
          <span
            class="size-[9px] rounded-full flex-shrink-0"
            :style="dotStyle(value)"
          />
          <span class="flex-1 truncate">{{ value }}</span>
          <Icon
            v-if="menu.current === value"
            icon="i-lucide-check"
            size="size-3 text-n-blue-text"
          />
        </button>
      </div>
    </div>

    <span v-if="menus.length" class="w-px h-5 bg-n-weak mx-1 flex-shrink-0" />

    <!-- Pills: Todos / Activos / saved segments -->
    <button
      type="button"
      :class="[PILL_CLASS, pillClass(isIndexView)]"
      @click="goToAll"
    >
      <span>{{ t('CONTACTS_LAYOUT.HEADER.SCOPE_FILTER.ALL_PILL') }}</span>
      <span
        v-if="totalCount"
        class="text-[10.5px] font-semibold"
        :class="countClass(isIndexView)"
      >
        {{ totalCount }}
      </span>
    </button>
    <button
      type="button"
      :class="[PILL_CLASS, pillClass(isActiveView)]"
      @click="goToActive"
    >
      {{ t('CONTACTS_LAYOUT.HEADER.SCOPE_FILTER.ACTIVE') }}
    </button>
    <button
      v-for="segment in segments"
      :key="segment.id"
      type="button"
      :class="[PILL_CLASS, pillClass(currentSegmentId === segment.id)]"
      :title="segment.name"
      @click="goToSegment(segment.id)"
    >
      <span class="truncate">{{ segment.name }}</span>
      <span
        v-if="segmentCount(segment)"
        class="text-[10.5px] font-semibold"
        :class="countClass(currentSegmentId === segment.id)"
      >
        {{ segmentCount(segment) }}
      </span>
    </button>
  </div>
</template>
